import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ShieldCheck, Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waitlist() {
  const [role, setRole] = useState("guard");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email to get the app link.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        role,
        phone: phone.trim() || undefined,
      });
      setDone(true);
      toast.success("You're on the list. Check your inbox for confirmation.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="download" data-testid="waitlist-section" className="relative overflow-hidden border-t border-white/5 py-24 md:py-32">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500/8 blur-[140px]" />
      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono-tag text-[11px] text-amber-500">Launching soon on iOS & Android</p>
          <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl">
            Get the app.
            <span className="block text-amber-500">Earn the rating.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-slate-400">
            Join the launch list and we'll email your download link the moment GuardGig28
            goes live in your city. Guards and venues welcome — the rules apply to both.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mx-auto mt-12 max-w-xl"
        >
          {done ? (
            <div data-testid="waitlist-success" className="glow-amber flex flex-col items-center gap-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-10">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              <p className="font-display text-2xl font-bold uppercase text-slate-50">You're on the list</p>
              <p className="text-sm text-slate-400">
                A confirmation email is on its way. Remember rule 01.
              </p>
            </div>
          ) : (
            <form data-testid="waitlist-form" onSubmit={submit} className="space-y-4">
              <div className="flex justify-center gap-2">
                <button
                  type="button"
                  data-testid="waitlist-role-guard"
                  onClick={() => setRole("guard")}
                  className={`flex items-center gap-2 rounded-md px-5 py-3 font-display text-sm font-bold uppercase transition-all duration-200 ${
                    role === "guard" ? "bg-amber-500 text-[#07090e]" : "border border-white/10 text-slate-400 hover:text-slate-50"
                  }`}
                >
                  <ShieldCheck className="h-4 w-4" /> I'm a guard
                </button>
                <button
                  type="button"
                  data-testid="waitlist-role-venue"
                  onClick={() => setRole("venue")}
                  className={`flex items-center gap-2 rounded-md px-5 py-3 font-display text-sm font-bold uppercase transition-all duration-200 ${
                    role === "venue" ? "bg-amber-500 text-[#07090e]" : "border border-white/10 text-slate-400 hover:text-slate-50"
                  }`}
                >
                  <Building2 className="h-4 w-4" /> I'm a venue
                </button>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="waitlist-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-14 flex-1 rounded-md border border-white/10 bg-[#0f141c] px-5 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
                />
                <input
                  data-testid="waitlist-phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+44 phone (optional)"
                  className="h-14 rounded-md border border-white/10 bg-[#0f141c] px-5 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none sm:w-48"
                />
              </div>
              <button
                data-testid="waitlist-submit-btn"
                type="submit"
                disabled={loading}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-md bg-amber-500 px-8 font-display text-base font-bold uppercase text-[#07090e] transition-all duration-200 hover:bg-amber-400 hover:shadow-[0_0_30px_rgba(245,158,11,0.35)] disabled:opacity-50"
              >
                {loading ? "Joining…" : "Notify me"}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
              <p className="font-mono-tag text-[9px] text-slate-600">Confirmation by email. SMS app-link arrives once texting is live. No spam.</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
